import { trpc } from "@/util/trpc";
import type { QueryClient } from "@tanstack/query-core";
import { QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, httpLink, splitLink } from "@trpc/react-query";
import { useState } from "react";
import superjson from "superjson";

const splitlinkLambda = splitLink({
    condition(op) {
        // check for context property `skipBatch`
        return Boolean(op.context.skipBatch);
    },
    // when condition is true, use normal request
    true: httpLink({ url: `${getBaseUrl()}/api/trpc-lambda`, transformer: superjson }),
    // when condition is false, use batching
    false: httpBatchLink({ url: `${getBaseUrl()}/api/trpc-lambda`, transformer: superjson }),
});

const splitlinkEdge = splitLink({
    condition(op) {
        // check for context property `skipBatch`
        return Boolean(op.context.skipBatch);
    },
    // when condition is true, use normal request
    true: httpLink({ url: `${getBaseUrl()}/api/trpc-edge`, transformer: superjson }),
    // when condition is false, use batching
    false: httpBatchLink({ url: `${getBaseUrl()}/api/trpc-edge`, transformer: superjson }),
});

export default function TrpcProvider({ children, client }: { children: React.ReactNode; client: QueryClient }) {
    const [trpcClient] = useState(() =>
        trpc.createClient({
            links: [
                (runtime) => {
                    // initialize the different links for different targets
                    const servers = {
                        lambda: splitlinkLambda(runtime),
                        edge: splitlinkEdge(runtime),
                    };
                    return (ctx) => {
                        const { op } = ctx;
                        // split the path by `.` as the first part will signify the server target name
                        const pathParts = op.path.split(".");

                        // first part of the query should be `server1` or `server2`
                        const serverName = pathParts.shift() as keyof typeof servers;

                        // combine the rest of the parts of the paths
                        // -- this is what we're actually calling the target server with
                        const path = pathParts.join(".");

                        const link = servers[serverName];

                        return link({
                            ...ctx,
                            op: {
                                ...op,
                                // override the target path with the prefix removed
                                path,
                            },
                        });
                    };
                },
            ],
        }),
    );
    return (
        <trpc.Provider client={trpcClient} queryClient={client}>
            <QueryClientProvider client={client}>{children}</QueryClientProvider>
        </trpc.Provider>
    );
}

function getBaseUrl() {
    if (typeof window !== "undefined") {
        // In the browser, we return a relative URL
        return "";
    }
    // When rendering on the server, we return an absolute URL

    // reference for vercel.com
    if (process.env.VERCEL_URL !== undefined) {
        return `https://${process.env.VERCEL_URL}`;
    }

    // assume localhost
    return `http://localhost:${process.env.PORT ?? 3000}`;
}
